import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import '../Games.css';
import {Player} from '../common/types';
import {loadJSON, saveJSON} from '../common/storage';
import PlayerSetup from '../common/PlayerSetup';
import {OPENING_LINES} from './storyPrompts';

const MIN_PLAYERS = 3;
const PLAYERS_KEY = 'games_players';
const LAP_OPTIONS = [1, 2, 3];

type Stage = 'setup' | 'ready' | 'writing' | 'reveal';

interface Entry {
    playerId: string;
    playerName: string;
    text: string;
}

const StoryChainGame: React.FC = () => {
    const [players, setPlayers] = useState<Player[]>(() => loadJSON(PLAYERS_KEY, []));
    const [laps, setLaps] = useState(1);

    const [stage, setStage] = useState<Stage>('setup');
    const [opening, setOpening] = useState('');
    const [entries, setEntries] = useState<Entry[]>([]);
    const [stepIndex, setStepIndex] = useState(0);
    const [draft, setDraft] = useState('');

    const updatePlayers = (next: Player[]) => {
        setPlayers(next);
        saveJSON(PLAYERS_KEY, next);
    };

    const totalSteps = players.length * laps;
    const currentPlayer = players[stepIndex % players.length];
    const previousText = stepIndex === 0 ? opening : entries[stepIndex - 1].text;

    const startGame = () => {
        if (players.length < MIN_PLAYERS) return;
        const line = OPENING_LINES[Math.floor(Math.random() * OPENING_LINES.length)];
        setOpening(line);
        setEntries([]);
        setStepIndex(0);
        setDraft('');
        setStage('ready');
    };

    const submitSentence = () => {
        const text = draft.trim();
        if (!text || !currentPlayer) return;
        setEntries(e => [...e, {playerId: currentPlayer.id, playerName: currentPlayer.name, text}]);
        setDraft('');
        const nextIndex = stepIndex + 1;
        if (nextIndex >= totalSteps) {
            setStage('reveal');
        } else {
            setStepIndex(nextIndex);
            setStage('ready');
        }
    };

    const backToSetup = () => {
        setStage('setup');
        setEntries([]);
        setStepIndex(0);
    };

    return (
        <div className={`GmPage ${stage === 'setup' ? '' : 'GmPage--noBar'}`}>
            <div className="GmContainer">
                <div className="GmTopBar">
                    <Link to="/games" className="GmBack">← Игры</Link>
                    <h1 className="GmTitle" style={{margin: 0}}>Фанты-история</h1>
                </div>

                {stage === 'setup' && (
                    <>
                        <p className="GmSubtitle">
                            По кругу каждый добавляет одно предложение к истории, видя только предыдущее.
                            В финале зачитайте получившийся абсурдный рассказ целиком.
                        </p>

                        <PlayerSetup players={players} setPlayers={updatePlayers} minPlayers={MIN_PLAYERS}/>

                        <div className="GmCard">
                            <h3 className="GmCard-title">Кругов истории</h3>
                            <div className="GmBtnRow">
                                {LAP_OPTIONS.map(n => (
                                    <button
                                        key={n}
                                        className={`GmBtn ${laps === n ? 'GmBtn--primary' : ''}`}
                                        onClick={() => setLaps(n)}
                                    >{n}</button>
                                ))}
                            </div>
                            {players.length > 0 && (
                                <p className="GmHint">Итоговая история — {players.length * laps} предложений.</p>
                            )}
                        </div>

                        <div className="GmBottomBar">
                            <div className="GmBottomBar-inner">
                                <button
                                    className="GmBtn GmBtn--primary GmBtn--big"
                                    disabled={players.length < MIN_PLAYERS}
                                    onClick={startGame}
                                >
                                    Начать историю
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {stage === 'ready' && currentPlayer && (
                    <div className="GmPassCard">
                        <p className="GmPassCard-hint">Передайте телефон игроку</p>
                        <h2 className="GmPassCard-name">{currentPlayer.name}</h2>
                        <button className="GmBtn GmBtn--primary GmBtn--big" onClick={() => setStage('writing')}>
                            Я — {currentPlayer.name}, посмотреть и продолжить
                        </button>
                        <p className="GmPassCard-progress">{stepIndex + 1} / {totalSteps}</p>
                    </div>
                )}

                {stage === 'writing' && currentPlayer && (
                    <div className="GmCard">
                        <p className="GmHint GmHint--warn">⚠ Не показывайте экран остальным!</p>
                        <h3 className="GmCard-title">{stepIndex === 0 ? 'Начало истории' : 'Предыдущее предложение'}</h3>
                        <p className="GmSituationText">{previousText}</p>
                        <p className="GmHint" style={{marginTop: 14}}>Придумай одно предложение — продолжение истории</p>
                        <textarea
                            className="GmInput"
                            style={{marginTop: 8, minHeight: 90, resize: 'vertical', fontFamily: 'inherit'}}
                            placeholder="Твоё продолжение"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                        />
                        <div style={{marginTop: 16}}>
                            <button className="GmBtn GmBtn--primary GmBtn--big" disabled={!draft.trim()} onClick={submitSentence}>
                                {stepIndex + 1 >= totalSteps ? 'Готово, показать историю' : 'Скрыть и передать дальше'}
                            </button>
                        </div>
                    </div>
                )}

                {stage === 'reveal' && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Вот что получилось</h3>
                        <p className="GmSituationText" style={{marginBottom: 12}}>{opening}</p>
                        <div className="GmRevealList">
                            {entries.map((entry, i) => (
                                <div className="GmRevealItem" key={i}>{entry.text}</div>
                            ))}
                        </div>
                        <div className="GmBtnRow" style={{marginTop: 16}}>
                            <button className="GmBtn GmBtn--primary" onClick={startGame}>Новая история</button>
                            <button className="GmBtn" onClick={backToSetup}>Изменить игроков</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StoryChainGame;
